import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 泛知识与生活百科 - 竖屏「杂志卡片」风格
 *
 * 视觉特点：大色块分区、编辑感排版、数字高亮、对比色系、现代杂志美学
 * 适合：生活小常识、排行/清单、对比推荐、日常科学等内容
 * 尺寸：1080x1920 竖屏
 */

interface RankItem {
  rank: number;
  title: string;
  subtitle: string;
  highlight: string;
  highlightColor: string;
}

interface LifeMagazineProps {
  category: string;
  headline: string;
  subheadline: string;
  items: RankItem[];
  editorNote: string;
  bgColor: string;
  accentColor: string;
}

const DEFAULT_PROPS: LifeMagazineProps = {
  category: 'HEALTH & WELLNESS',
  headline: '提升免疫力\n最有效的 5 件事',
  subheadline: '来自哈佛医学院的建议',
  items: [
    { rank: 1, title: '充足睡眠', subtitle: '每晚 7-9 小时', highlight: '#1', highlightColor: '#dc2626' },
    { rank: 2, title: '规律运动', subtitle: '每周 150 分钟中等强度', highlight: '#2', highlightColor: '#ea580c' },
    { rank: 3, title: '均衡饮食', subtitle: '多蔬果、全谷物、优质蛋白', highlight: '#3', highlightColor: '#d97706' },
    { rank: 4, title: '压力管理', subtitle: '冥想、深呼吸、社交支持', highlight: '#4', highlightColor: '#65a30d' },
    { rank: 5, title: '戒烟限酒', subtitle: '烟酒直接损害免疫细胞', highlight: '#5', highlightColor: '#0891b2' },
  ],
  editorNote: '以上方法简单却被大多数人忽略',
  bgColor: '#1c1917',
  accentColor: '#dc2626',
};

export const LifeMagazineTemplate: React.FC<Partial<LifeMagazineProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Category & headline (0-50)
  const categoryOpacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const headlineProgress = spring({ frame: frame - 10, fps, config: { damping: 18, stiffness: 80 } });
  const subheadlineOpacity = interpolate(frame, [40, 60], [0, 1], { extrapolateRight: 'clamp' });

  // Items
  const itemStart = 70;
  const itemInterval = 55;

  // Editor note
  const noteStart = itemStart + p.items.length * itemInterval + 20;
  const noteOpacity = interpolate(frame, [noteStart, noteStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Decorative line animation
  const lineHeight = interpolate(frame, [10, 80], [0, 300], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: p.bgColor,
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Large decorative number background */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          right: -40,
          fontSize: 500,
          fontWeight: 900,
          color: 'rgba(255,255,255,0.02)',
          lineHeight: 0.9,
          fontFamily: '"Inter", "Helvetica Neue", sans-serif',
        }}
      >
        {p.items.length}
      </div>

      {/* Accent line - left edge */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 40,
          width: 4,
          height: lineHeight,
          background: p.accentColor,
          borderRadius: 2,
        }}
      />

      {/* Category */}
      <div
        style={{
          position: 'absolute',
          top: 90,
          left: 70,
          opacity: categoryOpacity,
        }}
      >
        <span
          style={{
            fontSize: 40,
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: p.accentColor,
            textTransform: 'uppercase',
          }}
        >
          {p.category}
        </span>
      </div>

      {/* Headline */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          left: 70,
          right: 60,
          opacity: headlineProgress,
          transform: `translateY(${(1 - headlineProgress) * 30}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 900,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: '-0.03em',
            whiteSpace: 'pre-line',
          }}
        >
          {p.headline}
        </h1>
      </div>

      {/* Subheadline */}
      <div
        style={{
          position: 'absolute',
          top: 400,
          left: 70,
          opacity: subheadlineOpacity,
        }}
      >
        <span style={{ fontSize: 44, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
          {p.subheadline}
        </span>
      </div>

      {/* Divider */}
      <div
        style={{
          position: 'absolute',
          top: 480,
          left: 70,
          width: interpolate(frame, [55, 75], [0, 200], { extrapolateRight: 'clamp' }),
          height: 1,
          background: 'rgba(255,255,255,0.15)',
        }}
      />

      {/* Ranked items */}
      <div
        style={{
          position: 'absolute',
          top: 550,
          left: 50,
          right: 50,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {p.items.map((item, i) => {
          const start = itemStart + i * itemInterval;
          const cardScale = spring({
            frame: frame - start,
            fps,
            from: 0.9,
            to: 1,
            config: { damping: 14, stiffness: 130, mass: 0.5 },
          });
          const cardOpacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
          const slideX = interpolate(frame, [start, start + 20], [60, 0], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                opacity: cardOpacity,
                transform: `translateX(${slideX}px) scale(${cardScale})`,
                marginBottom: 30,
              }}
            >
              {/* Rank number */}
              <div
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: 20,
                  background: item.highlightColor,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: 40,
                  fontWeight: 900,
                  flexShrink: 0,
                  fontFamily: '"Inter", "Helvetica Neue", sans-serif',
                }}
              >
                {item.highlight}
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 38,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: 36,
                    color: 'rgba(255,255,255,0.45)',
                    marginTop: 4,
                  }}
                >
                  {item.subtitle}
                </div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.15)' }}>→</div>
            </div>
          );
        })}
      </div>

      {/* Editor note */}
      <div
        style={{
          position: 'absolute',
          bottom: 120,
          left: 50,
          right: 50,
          opacity: noteOpacity,
        }}
      >
        <div
          style={{
            padding: '20px 28px',
            borderLeft: `4px solid ${p.accentColor}`,
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          <div style={{ fontSize: 36, color: p.accentColor, fontWeight: 600, marginBottom: 6, letterSpacing: '0.1em' }}>
            EDITOR'S NOTE
          </div>
          <div style={{ fontSize: 34, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{p.editorNote}</div>
        </div>
      </div>

      {/* Page indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'inline-flex', gap: 8 }}>
          {[0, 1, 2].map((d) => (
            <div
              key={d}
              style={{
                width: d === 0 ? 24 : 8,
                height: 8,
                borderRadius: 4,
                background: d === 0 ? p.accentColor : 'rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: p.accentColor,
        }}
      />
    </AbsoluteFill>
  );
};
